import React from 'react'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import { H2, H4 } from '@jarvis.oddle/core/components/Typo'
import Flex from '@jarvis.oddle/core/components/Flex'
import Div from '@jarvis.oddle/core/components/Div'
import { RatioImage } from '@jarvis.oddle/core/components/Image'
import usePrevious from '@jarvis.oddle/core/hooks/usePrevious'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import {
  colorConstants,
  iconSizes,
  marginSizes,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import IconInstagram from '@jarvis.oddle/core/components/Icons/IconInstagram'
import { shallowEqual } from 'src/utils/object'
import { isEmpty } from 'src/utils/string'
import Loading from '@jarvis.oddle/core/components/Loading'
import { isInstagramTokenExpired } from '@jarvis.oddle/core/tools/instagram'
import LazyLoad from 'react-lazyload'
import { getIgUserMediaApi } from 'api/instagram/client'
import {
  refreshIgTokenApi,
  refreshPreviewIgTokenApi
} from 'api/customisation/client'
import styled from 'styled-components'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectSections } from 'src/redux/customisation'
import { selectPreviewMode } from 'src/redux/customisation/selector'
import { selectDetailMenuSite } from 'src/redux/meta'

const InstagramFeedContainer = styled(Flex).attrs(() => ({
  marginTop: marginSizes.large,
  marginBottom: marginSizes.large,
  flexDirection: 'column'
}))``

const CenterContainer = styled(Flex).attrs(() => ({
  justifyContent: 'center',
  alignItems: 'center',
  margin: marginSizes.large
}))``

const HeaderContainer = styled(Div)`
  text-align: center;
`

const Overlay = styled(Flex).attrs(() => ({
  justifyContent: 'center',
  alignItems: 'center'
}))`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 300ms;
  /* stylelint-disable declaration-colon-newline-after */
  ${(props) =>
    props.spacing
      ? `
    top: ${padding[paddingSizes.small]};
    right: ${padding[paddingSizes.small]};
    bottom: ${padding[paddingSizes.small]};
    left: ${padding[paddingSizes.small]};
  `
      : ''};
  /* stylelint-enable declaration-colon-newline-after */
`

const ImageContainer = styled.a<{
  spacing: boolean
}>`
  position: relative;
  display: block;
  flex: 0 0 ${100 / 2}%;
  padding: ${(props) => (props.spacing ? padding[paddingSizes.small] : 0)};
  ${breakpoint('tablet')`
    flex: 0 0 ${100 / 6}%;
  `}

  &:hover {
    ${Overlay} {
      opacity: 1;
    }
  }
`

interface InstagramFeedProps {
  id: string
}

interface InstagramFeedSection {
  id: string
  name: string
  type: string
  settings: {
    headerText: string
    subheaderText: string
    numberOfPosts: number
    showSpacing: boolean
    instagramAccessToken: {
      igUserId: string
      igToken: string
      igTokenExpTime: string
    }
  }
  blocks?: {
    [key: string]: unknown
  }
  blocksOrder?: string[]
  createdAt: string
  updatedAt: string
}

const initialState = {
  fetching: false,
  data: [],
  error: null
}

const stateReducer = (state, action) => {
  switch (action.type) {
    case 'ON_FETCHING':
      return {
        ...state,
        fetching: true
      }
    case 'ON_SUCCESS':
      return {
        ...state,
        data: action.payload.data,
        error: null,
        fetching: false
      }
    case 'ON_ERROR':
      return {
        ...state,
        error: action.payload.error,
        data: [],
        fetching: false
      }
    case 'ON_INITIALIZE':
      return initialState
    default:
      return state
  }
}

const fetchIgUserMedia = async ({ igToken }) => {
  const accumulatedMedia = []
  let after = ''

  // act as a circuit breaker, only tries 10 times
  for (let iteration = 0; iteration < 10; iteration++) {
    // this api will return either image, video, or album from the user media feed
    const { data, paging } = await getIgUserMediaApi({ igToken, after })

    for (const media of data) {
      const { media_type: mediaType } = media || {}
      // eliminate video and album, only interested in image
      if (mediaType === 'IMAGE') {
        accumulatedMedia.push(media)
      }
    }

    // since Oddle Shop only allows user to display maximum of 12 Instagram posts,
    // stop retrieving more media when 12 posts have been accumulated or
    // no media can be retrieved from the user feed anymore
    if (accumulatedMedia.length >= 12 || isEmpty(paging.next)) {
      break
    }

    // since getIgUserMediaApi is paginated, we pass this value to access the next page
    after = paging.cursors.after
  }

  return accumulatedMedia
}

const InstagramFeed = ({ id }: InstagramFeedProps): JSX.Element => {
  const previewMode = useAppSelector(selectPreviewMode)
  const { settings } = useAppSelector(selectSections)[
    id
  ] as InstagramFeedSection
  const {
    menu: { id: _menuId }
  } = useAppSelector(selectDetailMenuSite)
  const { t } = React.useContext(TranslationContext)
  const [accessToken, setAccessToken] = React.useState<{
    igToken?: string
    igTokenExpTime?: string
    igUserId?: string
  }>({})
  const prevAccessToken = usePrevious(accessToken)
  const instagramAccessToken = React.useMemo(() => {
    const { instagramAccessToken } = settings || {}
    const { igUserId, igToken, igTokenExpTime } = instagramAccessToken || {}
    return {
      igUserId,
      igToken,
      igTokenExpTime
    }
  }, [settings])

  const [state, setState] = React.useReducer(stateReducer, initialState)
  const { fetching, data, error } = state

  // TODO: replace with optional chaining
  const menuId = (() => {
    try {
      return _menuId
    } catch (_) {
      return undefined
    }
  })()

  const { headerText, subheaderText, numberOfPosts, showSpacing } =
    settings || {}

  const onRefreshToken = React.useCallback(
    async (accessToken, previewMode) => {
      // refresh the token silently in background, no loading indicator is meant to be shown

      const { igUserId, igToken } = accessToken
      try {
        const payload = {
          igUserId,
          igToken
        }
        // tyescpript throw this error
        const data = previewMode
          ? await refreshPreviewIgTokenApi({ req: {}, menuId, payload })
          : await refreshIgTokenApi({ req: {}, menuId, payload })

        // after successfuly refresh the token, update the token stored in the state
        // with the newly updated token to trigger refetching the instagram feeds
        if (data.refreshed) {
          setAccessToken({
            igUserId: data.igUserId,
            igToken: data.igToken,
            igTokenExpTime: data.igTokenExpTime
          })
        }
      } catch (error) {
        // fail silently
      }
    },
    [menuId]
  )

  const onFetchData = React.useCallback(async (accessToken) => {
    const { igUserId, igToken } = accessToken || {}
    if (isEmpty(igUserId) || isEmpty(igToken)) {
      setState({ type: 'ON_INITIALIZE' })
      return
    }

    try {
      setState({ type: 'ON_FETCHING' })
      const data = await fetchIgUserMedia({ igToken })
      setState({ type: 'ON_SUCCESS', payload: { data } })
    } catch (error) {
      setState({ type: 'ON_ERROR', payload: { error } })
    }
  }, [])

  const onImageClick = (e, item) => {
    if (!previewMode) return
    e.preventDefault()
    window.confirm(
      t(
        'This link cannot be opened in customisation preview. It will be opened in a new window (_target_).\nClick OK to continue',
        { target: item.link }
      )
    ) && window.open(item.link, '_blank')
  }

  React.useEffect(() => {
    // fetch new media everytime user change their Instagram account
    if (!shallowEqual(accessToken, prevAccessToken)) {
      const { igTokenExpTime } = accessToken
      if (isInstagramTokenExpired(igTokenExpTime)) {
        onRefreshToken(accessToken, previewMode)
      } else {
        onFetchData(accessToken)
      }
    }
  }, [accessToken, onFetchData, onRefreshToken, prevAccessToken, previewMode])

  React.useEffect(() => {
    const { igTokenExpTime } = accessToken

    // when user lands on the page and the token is expiring,
    // call Oddle Shop Editor endpoint to refresh and update the token
    if (isInstagramTokenExpired(igTokenExpTime)) {
      onRefreshToken(accessToken, previewMode)
    }

    onFetchData(accessToken)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    // which preview mode, the useState for accessToken not works
    // because the accessToken will be passed from frame to Shop after this component rendered
    // so we need to add listener for instagramAccessToken
    setAccessToken(instagramAccessToken)
  }, [instagramAccessToken])

  return !error ? (
    <InstagramFeedContainer>
      <HeaderContainer>
        <H2 bold sectionHeading>
          {headerText}
        </H2>
        <H4 sectionHeading>{subheaderText}</H4>
      </HeaderContainer>
      {fetching && (
        <CenterContainer>
          <Loading />
        </CenterContainer>
      )}
      {!fetching && (
        <Flex width='100%' flexWrap='wrap' justifyContent='center'>
          {data.slice(0, numberOfPosts).map((item) => (
            <ImageContainer
              key={item.id}
              spacing={showSpacing === true}
              href={item.permalink}
              target='_blank'
              onClick={(e) => onImageClick(e, item)}
            >
              <LazyLoad once offset={200}>
                <RatioImage src={item.media_url} alt={item.username} />
              </LazyLoad>
              <Overlay spacing={showSpacing === true}>
                <IconInstagram
                  fill={colorConstants.white}
                  iconSize={iconSizes.extraLarge}
                />
              </Overlay>
            </ImageContainer>
          ))}
        </Flex>
      )}
    </InstagramFeedContainer>
  ) : null
}

export default InstagramFeed
